<script lang="ts">
  import { t } from '$i18n/index';
  import { app } from '$ui/app-state.svelte';
  import { navigate } from '$ui/router';
  import { claimPlacementReoffer, loadDraft, saveDraft } from '$app/onboarding-service';

  /**
   * The single sanctioned re-offer of the placement ritual (spec §14).
   *
   * The risk this guards against is named in the spec's own risk table: the
   * ritual is the keystone step, so there is real pressure to ask again — and
   * an app that asks twice about the same thing is nagging, which is a dark
   * pattern this product does not get to use.
   *
   * **The event is written when the offer is shown, not when it is answered.**
   * That is the whole design. Recording it on answer would let someone close the
   * app mid-offer and be asked again on every launch; recording it on display
   * makes a second offer impossible by construction rather than by care. The
   * cost is that an offer dismissed by force-quitting is spent — which is the
   * right way round: a missed offer costs the user nothing, a repeated one costs
   * them the trust the whole product runs on.
   */

  let showing = $state(false);
  let dismissed = $state(false);
  let checked = $state(false);

  $effect(() => {
    if (checked) return;
    // `completed` is false until the log has been replayed, so this doubles as
    // "wait for derived state" without a separate loading flag.
    if (!app.derived.onboarding.completed) return;
    checked = true;

    const { placementRitualDone, placementReoffered } = app.derived.onboarding;
    if (placementRitualDone || placementReoffered) return;

    // Claim first, show second. The claim is atomic, so two tabs cannot both
    // decide to offer; and `showing` is local state rather than derived,
    // because recording the offer immediately flips `placementReoffered` to
    // true and a derived condition would hide the banner in the tick it
    // appeared — spending the offer without anyone seeing it.
    void (async () => {
      const ctx = await app.context();
      showing = await claimPlacementReoffer(ctx);
      if (showing) await app.refresh();
    })();
  });
</script>

{#if showing && !dismissed}
  <aside>
    <p>{t('onboard.placement.reoffer')}</p>
    <div class="actions">
      <button type="button" onclick={() => (dismissed = true)}>
        {t('onboard.placement.reofferNo')}
      </button>
      <button
        type="button"
        onclick={() => {
          dismissed = true;
          void (async () => {
            // Land on the placement step, not back at "what matters to you".
            const ctx = await app.context();
            const draft = await loadDraft(ctx);
            await saveDraft(ctx, { ...draft, step: 'placement' });
            navigate('onboard');
          })();
        }}
      >
        {t('onboard.placement.reofferDo')}
      </button>
    </div>
  </aside>
{/if}

<style>
  aside {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    flex-wrap: wrap;
    padding: var(--space-3) var(--space-4);
    background: var(--c-surface);
    border-bottom: 1px solid var(--c-line);
    color: var(--c-ink-muted);
    font-size: var(--step--1);
  }

  .actions {
    display: flex;
    gap: var(--space-2);
  }

  button {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius);
    border: 1px solid var(--c-line);
    color: var(--c-ink);
    min-height: var(--tap-min);
    font-size: var(--step--1);
  }
</style>
